import { Model, stringifyGraph } from '@/clients/http'

import { MediaHttpParams, MediaResponses } from '@/types'

export class MediaModel extends Model {
  public static readonly correlations = {
    GET_ROOT_FOLDERS: 'MEDIA_GET_ROOT_FOLDERS',
    GET_FOLDER: 'MEDIA_GET_FOLDER',
    CREATE_FOLDER: 'MEDIA_CREATE_FOLDER',
    UPLOAD_IMAGE: 'MEDIA_UPLOAD_IMAGE',
    UPLOAD_DOCUMENT: 'MEDIA_UPLOAD_DOCUMENT',
    UPLOAD_VIDEO: 'MEDIA_UPLOAD_VIDEO',
    DELETE_MEDIA: 'MEDIA_DELETE_MEDIA',
    DELETE_FOLDER: 'MEDIA_DELETE_FOLDER',
    OPTIMIZE_IMAGE: 'MEDIA_OPTIMIZE_IMAGE',
    CONVERT_FILE: 'MEDIA_CONVERT_FILE',
  }

  public static readonly endpoints = {
    getRootFolders: () => '/api/backoffice/media/root-folders',
    getFolder: (id: string) =>
      `/api/backoffice/media/folder/${id}?graph_enc=${stringifyGraph({ '*': true, folders: { '*': true }, files: { '*': true } })}`,
    createFolder: () => '/api/backoffice/media/folder',
    uploadImage: () => '/api/backoffice/media/image',
    uploadDocument: () => '/api/backoffice/media/document',
    uploadVideo: () => '/api/backoffice/media/video',
    deleteMedia: (id: string) => `/api/backoffice/media/${id}`,
    deleteFolder: (id: string) => `/api/backoffice/media/folder/${id}`,
    optimizeImage: (id: string) => `/api/backoffice/media/${id}/optimize`,
    convertFile: (id: string) => `/api/backoffice/media/${id}/convert`,
  }

  public static readonly getRootFolders = async (props?: MediaHttpParams.GetRootFolders) => {
    return this.createHttpRequest<MediaResponses.GetRootFolders>({
      correlationKey: this.correlations.GET_ROOT_FOLDERS,
      payload: {
        method: 'get',
        url: this.endpoints.getRootFolders(),
        params: props?.query,
        useBearerToken: true,
      },
    })
  }

  public static readonly getFolder = async (props: MediaHttpParams.GetFolder) => {
    return this.createHttpRequest<MediaResponses.GetFolder>({
      correlationKey: this.correlations.GET_FOLDER,
      payload: {
        method: 'get',
        url: this.endpoints.getFolder(props.id),
        params: {
          graph_enc: '{*,folders{*},files{*}}',
          ...props.query,
        },
        useBearerToken: true,
      },
    })
  }

  public static readonly createFolder = async (props: MediaHttpParams.CreateFolder) => {
    return this.createHttpRequest<MediaResponses.CreateFolder>({
      correlationKey: this.correlations.CREATE_FOLDER,
      payload: {
        method: 'post',
        url: this.endpoints.createFolder(),
        body: props.body,
        useBearerToken: true,
      },
    })
  }

  public static readonly uploadImage = async (props: MediaHttpParams.Upload) => {
    return this.createHttpRequest<MediaResponses.Upload>({
      correlationKey: this.correlations.UPLOAD_IMAGE,
      payload: {
        method: 'post',
        url: this.endpoints.uploadImage(),
        body: props.body,
        useBearerToken: true,
        multipart: true,
        onUploadProgress: props.onUploadProgress,
      },
    })
  }

  public static readonly uploadDocument = async (props: MediaHttpParams.Upload) => {
    return this.createHttpRequest<MediaResponses.Upload>({
      correlationKey: this.correlations.UPLOAD_DOCUMENT,
      payload: {
        method: 'post',
        url: this.endpoints.uploadDocument(),
        body: props.body,
        useBearerToken: true,
        multipart: true,
        onUploadProgress: props.onUploadProgress,
      },
    })
  }

  public static readonly uploadVideo = async (props: MediaHttpParams.Upload) => {
    return this.createHttpRequest<MediaResponses.Upload>({
      correlationKey: this.correlations.UPLOAD_VIDEO,
      payload: {
        method: 'post',
        url: this.endpoints.uploadVideo(),
        body: props.body,
        useBearerToken: true,
        multipart: true,
        onUploadProgress: props.onUploadProgress,
      },
    })
  }

  public static readonly deleteMedia = async (props: MediaHttpParams.Delete) => {
    return this.createHttpRequest<MediaResponses.Delete>({
      correlationKey: this.correlations.DELETE_MEDIA,
      payload: {
        method: 'delete',
        url: this.endpoints.deleteMedia(props.id),
        useBearerToken: true,
      },
    })
  }

  public static readonly deleteFolder = async (props: MediaHttpParams.Delete) => {
    return this.createHttpRequest<MediaResponses.Delete>({
      correlationKey: this.correlations.DELETE_FOLDER,
      payload: {
        method: 'delete',
        url: this.endpoints.deleteFolder(props.id),
        useBearerToken: true,
      },
    })
  }

  public static readonly optimizeImage = async (props: MediaHttpParams.OptimizeImage) => {
    return this.createHttpRequest<MediaResponses.OptimizeImage>({
      correlationKey: this.correlations.OPTIMIZE_IMAGE,
      payload: {
        method: 'post',
        url: this.endpoints.optimizeImage(props.id),
        body: props.body,
        useBearerToken: true,
      },
    })
  }

  public static readonly convertFile = async (props: MediaHttpParams.ConvertFile) => {
    return this.createHttpRequest<MediaResponses.ConvertFile>({
      correlationKey: this.correlations.CONVERT_FILE,
      payload: {
        method: 'post',
        url: this.endpoints.convertFile(props.id),
        body: props.body,
        useBearerToken: true,
      },
    })
  }

  public static readonly addExternalFile = async (props: MediaHttpParams.AddExternalFile) => {
    return this.createHttpRequest<MediaResponses.AddExternalFile>({
      correlationKey: 'MEDIA_ADD_EXTERNAL',
      payload: {
        method: 'post',
        url: '/api/backoffice/media/external',
        body: props.body,
        useBearerToken: true,
      },
    })
  }
}
